import React, { useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ActivityIndicator,
  Platform,
  ViewStyle,
  Keyboard,
} from 'react-native';
import { useSafeAreaInsets, initialWindowMetrics } from 'react-native-safe-area-context';
import { UnifiedModal, ModalButton } from '@/components/common/UnifiedModal';
import { useResponsive } from '@/hooks/common/useResponsive';
import { PostItemFormData, INITIAL_POST_ITEM_FORM } from '@/types/residents/marketplaceListing';
import { showConfirm, showAlert } from '@/utils/common/alert';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import {
  StepItemDetails,
  StepMedia,
  StepPriceCondition,
  StepPickupPreferences,
  StepSuccess,
} from './post-item';
import {
  useCreateListingMutation,
  useUpdateListingMutation,
  mapConditionToBackend,
  mapPickupOptionsToBackend
} from '@/store/api';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

type PostItemStep = 'details' | 'media' | 'price' | 'pickup' | 'success';

const STEPS: PostItemStep[] = ['details', 'media', 'price', 'pickup', 'success'];
const TOTAL_STEPS = STEPS.length;

interface PostItemModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  initialData?: PostItemFormData;
  isEditMode?: boolean;
  listingId?: string;
  propertyId?: number;
  /** Render as an embedded screen instead of a standalone Modal (avoids iOS keyboard issues). */
  asScreen?: boolean;
}

export const PostItemModal: React.FC<PostItemModalProps> = ({
  visible,
  onClose,
  onSubmitSuccess,
  initialData,
  isEditMode = false,
  listingId,
  propertyId,
  asScreen = false,
}) => {
  const { colors } = useThemeColors();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const insets = useSafeAreaInsets();
  const safeTop = Platform.OS === 'android'
    ? Math.max(insets.top, initialWindowMetrics?.insets?.top ?? 0)
    : insets.top;

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState<PostItemFormData>(initialData || INITIAL_POST_ITEM_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  React.useEffect(() => {
    if (Platform.OS !== 'android') return;
    const showSub = Keyboard.addListener('keyboardDidShow', () => setIsKeyboardVisible(true));
    const hideSub = Keyboard.addListener('keyboardDidHide', () => setIsKeyboardVisible(false));
    return () => { showSub.remove(); hideSub.remove(); };
  }, []);

  const [createListing] = useCreateListingMutation();
  const [updateListing] = useUpdateListingMutation();

  React.useEffect(() => {
    if (visible) {
      setFormData(initialData || INITIAL_POST_ITEM_FORM);
      setCurrentStepIndex(0);
    }
  }, [visible, initialData]);

  const currentStep = STEPS[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastFormStep = currentStep === 'pickup';
  const isSuccessStep = currentStep === 'success';
  const progressPercent = currentStepIndex === 0 ? 0 : (currentStepIndex / (TOTAL_STEPS - 1)) * 100;

  const handleFormChange = useCallback((updates: Partial<PostItemFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const resetAndClose = useCallback(() => {
    setCurrentStepIndex(0);
    setFormData(INITIAL_POST_ITEM_FORM);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(async () => {
    if (isSuccessStep) {
      resetAndClose();
      return;
    }

    const hasData =
      formData.title.trim() !== '' ||
      formData.description.trim() !== '' ||
      formData.images.length > 0 ||
      formData.pickupOptions.length > 0;

    if (hasData) {
      const confirmed = await showConfirm(
        'Discard Changes?',
        isEditMode
          ? 'Are you sure you want to discard your changes?'
          : 'Are you sure you want to discard your listing?',
        'Discard',
        'Cancel'
      );
      if (confirmed) {
        resetAndClose();
      }
    } else {
      resetAndClose();
    }
  }, [formData, isSuccessStep, isEditMode, resetAndClose]);

  const validateCurrentStep = useCallback((): boolean => {
    switch (currentStep) {
      case 'details':
        if (!formData.title.trim()) {
          showAlert('Title Required', 'Please enter a title for your item.');
          return false;
        }
        return true;
      case 'media':
        return true;
      case 'price':
        if (formData.priceType === 'set_price') {
          const price = parseFloat(formData.price);
          if (isNaN(price) || price < 0) {
            showAlert('Invalid Price', 'Please enter a valid price.');
            return false;
          }
        }
        return true;
      case 'pickup':
        if (formData.pickupOptions.length === 0) {
          showAlert('Pickup Required', 'Please select at least one pickup option.');
          return false;
        }
        return true;
      default:
        return true;
    }
  }, [currentStep, formData]);

  const handleNext = useCallback(() => {
    if (!validateCurrentStep()) return;

    if (isLastFormStep) {
      handleSubmit();
    } else {
      setCurrentStepIndex(prev => Math.min(prev + 1, TOTAL_STEPS - 1));
    }
  }, [validateCurrentStep, isLastFormStep]);

  const handleBack = useCallback(() => {
    setCurrentStepIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const priceValue = formData.priceType === 'free'
        ? 0
        : parseFloat(formData.price) || 0;

      const existingImages = formData.images.filter(img => img.serverId !== undefined);
      const newImages = formData.images.filter(img => img.serverId === undefined);

      const imagesToUpload = newImages.map((img, index) => ({
        uri: img.uri,
        type: img.type || 'image/jpeg',
        name: img.name || `image-${index}.jpg`,
        file: img.file,
      }));

      const existingImageIds = existingImages
        .map(img => img.serverId)
        .filter((id): id is number => id !== undefined);

      if (isEditMode && listingId) {
        await updateListing({
          id: listingId,
          title: formData.title,
          description: formData.description,
          price: priceValue,
          condition: mapConditionToBackend(formData.condition),
          pickupOption: mapPickupOptionsToBackend(formData.pickupOptions),
          existingImageIds: existingImageIds.length > 0 ? existingImageIds : undefined,
          images: imagesToUpload.length > 0 ? imagesToUpload : undefined,
        }).unwrap();

        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      } else {
        if (!propertyId) {
          showAlert('Error', 'Unable to create listing. Please try again.');
          setIsSubmitting(false);
          return;
        }

        const allImages = formData.images.map((img, index) => ({
          uri: img.uri,
          type: img.type || 'image/jpeg',
          name: img.name || `image-${index}.jpg`,
          file: img.file,
        }));

        await createListing({
          title: formData.title,
          description: formData.description,
          price: priceValue,
          condition: mapConditionToBackend(formData.condition),
          pickupOption: mapPickupOptionsToBackend(formData.pickupOptions),
          propertyId: propertyId,
          images: allImages.length > 0 ? allImages : undefined,
        }).unwrap();

        setCurrentStepIndex(TOTAL_STEPS - 1);

        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      }
    } catch (error: any) {
      console.error('Failed to submit listing:', error);
      showAlert(
        'Error',
        error?.data?.message || 'Failed to submit listing. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onSubmitSuccess, isEditMode, listingId, propertyId, isSubmitting, createListing, updateListing]);

  const handleBackToMarketplace = useCallback(() => {
    resetAndClose();
  }, [resetAndClose]);

  const isNextEnabled = useCallback((): boolean => {
    switch (currentStep) {
      case 'details':
        return formData.title.trim().length > 0;
      case 'media':
        return true;
      case 'price':
        if (formData.priceType === 'free') return true;
        const price = parseFloat(formData.price);
        return !isNaN(price) && price >= 0;
      case 'pickup':
        return formData.pickupOptions.length > 0;
      default:
        return true;
    }
  }, [currentStep, formData]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 'details':
        return (
          <StepItemDetails
            formData={formData}
            onFormChange={handleFormChange}
          />
        );
      case 'media':
        return (
          <StepMedia
            mediaItems={formData.images}
            onMediaItemsChange={(items) => handleFormChange({ images: items })}
          />
        );
      case 'price':
        return (
          <StepPriceCondition
            formData={formData}
            onFormChange={handleFormChange}
          />
        );
      case 'pickup':
        return (
          <StepPickupPreferences
            formData={formData}
            onFormChange={handleFormChange}
          />
        );
      case 'success':
        return <StepSuccess />;
      default:
        return null;
    }
  };

  const nextEnabled = isNextEnabled();
  // On Android, FullscreenContent handles bottom safe area via paddingBottom,
  // so footer only needs design spacing. On iOS, footer handles safe area itself.
  // When keyboard is visible on Android, add extra padding to clear manufacturer
  // keyboard toolbars (e.g. Samsung clipboard/voice bar) that sit above the keyboard.
  const KEYBOARD_TOOLBAR_BUFFER = 48;
  const safeBottom = Math.max(insets.bottom, initialWindowMetrics?.insets?.bottom ?? 0);
  const bottomPadding = Platform.OS === 'android'
    ? Math.max(safeBottom + 16, 32) + (isKeyboardVisible ? KEYBOARD_TOOLBAR_BUFFER : 0)
    : Math.max(safeBottom + 30, 40);

  const footerButtons = isSuccessStep ? (
    <View style={styles.footerRow}>
      <View style={styles.footerButtonWrapper}>
        <ModalButton
          label="Back to marketplace"
          variant="primary"
          onPress={handleBackToMarketplace}
        />
      </View>
    </View>
  ) : (
    <View style={styles.footerRow}>
      <View style={[styles.footerButtonWrapper, isFirstStep && styles.buttonHidden]}>
        <ModalButton
          label="Back"
          variant="secondary"
          onPress={handleBack}
          disabled={isFirstStep}
        />
      </View>
      {isSubmitting ? (
        <View style={styles.footerButtonWrapper}>
          <View style={[styles.loadingButton, { backgroundColor: PRIMITIVE_COLORS.resioBlue }]}>
            <ActivityIndicator size="small" color={colors.text.primary} />
          </View>
        </View>
      ) : (
        <View style={styles.footerButtonWrapper}>
          <ModalButton
            label={isLastFormStep ? (isEditMode ? 'Update' : 'Submit') : 'Next'}
            variant="primary"
            onPress={handleNext}
            disabled={!nextEnabled}
          />
        </View>
      )}
    </View>
  );

  const footerContent = (
    <View style={[styles.footerContainer, { borderTopColor: colors.separator, paddingBottom: bottomPadding }]}>
      {footerButtons}
    </View>
  );

  const modalContent = (
    <UnifiedModal
      visible={asScreen ? true : visible}
      onClose={handleClose}
      variant={isDesktop ? 'dialog' : 'fullscreen'}
      dialogSize="large"
      scrollable
      avoidKeyboard
      animateEntry={!asScreen}
      dismissOnBackdrop={false}
      footer={footerContent}
      footerStyle={footerPaddingReset}
      contentContainerStyle={scrollContentOverride}
    >
      <View style={[styles.progressBarBackground, { backgroundColor: colors.glass.buttonBg }]}>
        <View style={[styles.progressBarFill, { width: `${progressPercent}%` }]} />
      </View>
      {renderStepContent()}
    </UnifiedModal>
  );

  if (asScreen) {
    return (
      <View style={[styles.safeArea, { backgroundColor: colors.bg.modal }, Platform.OS === 'ios' && { paddingTop: safeTop }]}>
        {modalContent}
      </View>
    );
  }

  return modalContent;
};

const footerPaddingReset: ViewStyle = { paddingHorizontal: 0, paddingTop: 0, paddingBottom: 0, borderTopWidth: 0 };

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  footerContainer: {
    paddingHorizontal: 24,
    paddingTop: 16,
    borderTopWidth: 1,
  },
  progressBarBackground: {
    height: 8,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 16,
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: 8,
  },
  footerRow: {
    flexDirection: 'row',
    gap: 12,
  },
  footerButtonWrapper: {
    flex: 1,
  },
  buttonHidden: {
    opacity: 0,
  },
  loadingButton: {
    height: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.6,
  },
});

const scrollContentOverride: ViewStyle = { paddingHorizontal: 0, paddingBottom: 24, flexGrow: 1 };
